// START IMPORTS
// Core lib payments imports
import {
  PaymentsSelectionModal,
  PayPalRedirectionModal,
  RadioPaymentsModal,
  ViewOrderHistory,
} from '@ltvco/refresh-lib/payments';
// Theme imports
import { Grid, Box } from '@ltvco/refresh-lib/theme';
// Core lib v1 imports
import {
  AccountInformation,
  GenericCtaCard,
  LimitedPlanProgress,
  MyAccountHeader,
} from '@ltvco/refresh-lib/v1';
// Core lib v2 imports
import { AccountEditModals } from '@ltvco/refresh-lib/v2';
// Vendor imports
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';

// App-specific imports
import { constants } from 'appConstants';
import { useLimitedPlanInfo } from 'utils/useLimitedPlanInfo';

import {
  AccountInformationOptions,
  GenericCtaCardProps,
  editOptionClicked,
} from './BillingConstants';
// END IMPORTS

export function Billing({
  onCancelConfirmCancelModal,
}: {
  onCancelConfirmCancelModal?: () => void;
}) {
  // START HOOKS_AND_QUERIES
  const navigate = useNavigate();

  const {
    isLimitedUser,
    isUnlimitedUser,
    reportLimit,
    reportsRun,
    reportsRunPercentage,
    isLoading,
  } = useLimitedPlanInfo();
  // END HOOKS_AND_QUERIES

  // START STATE_MANAGEMENT
  const [openPaymentsSelectionModal, setOpenPaymentsSelectionModal] =
    useState<boolean>(false);

  const [openPaymentsUpdateModal, setOpenPaymentsUpdateModal] =
    useState<boolean>(false);

  //Paypal Redirection modal
  const [openPaypalRedirection, setOpenPaypalRedirection] =
    useState<boolean>(false);
  const [paypalState, setPaypalState] = useState<'success' | 'canceled'>(
    'success'
  );

  const [editing, setEditing] = useState<string | null>('');
  // END STATE_MANAGEMENT

  // START DERIVED_DATA_AND_UTILS
  const { ctaCardProps, length } = GenericCtaCardProps(navigate);
  // END DERIVED_DATA_AND_UTILS

  // START EVENT_HANDLERS
  const togglePaypalRedirectionModal = () =>
    setOpenPaypalRedirection(!openPaypalRedirection);

  const togglePaymentsSelectionModal = () =>
    setOpenPaymentsSelectionModal(!openPaymentsSelectionModal);

  const togglePaymentsUpdateModal = () => {
    setOpenPaymentsUpdateModal(!openPaymentsUpdateModal);
    if (!openPaymentsSelectionModal) {
      togglePaymentsSelectionModal();
    }
  };

  const finishEditing = () => {
    setEditing('');
  };
  // END EVENT_HANDLERS

  // START EFFECTS
  useEffect(() => {
    document.title = 'My Account - PeopleLooker';
  }, []);

  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    if (urlParams.get('bvppcanc')) {
      setPaypalState('canceled');
      setOpenPaypalRedirection(true);
      return;
    }
    if (urlParams.get('bvpp') && urlParams.get('token')) {
      setPaypalState('success');
      setOpenPaypalRedirection(true);
    }
  }, []);
  // END EFFECTS

  // START RENDER
  return (
    <>
      <MyAccountHeader isUnlimited={isUnlimitedUser} />
      {isLimitedUser && !isLoading && (
        <LimitedPlanProgress
          plansWithout5ReportBlocker={constants.data.plansWithout5ReportBlocker}
          validReportCountThreshold={constants.data.validReportCountThreshold}
          progressValue={reportsRunPercentage}
          reportsLimit={reportLimit}
          reportsRun={reportsRun}
          link="/upgrade/plan"
          blockUpgradeWindowThreshold={
            constants.data.blockUpgradeWindowThreshold
          }
        />
      )}
      <Grid container spacing={2} justifyContent="center">
        {[...Array(length)].map((_x, index) => (
          <Grid size={{ xs: 12, md: 6 }} key={index}>
            <GenericCtaCard {...ctaCardProps[index]}></GenericCtaCard>
          </Grid>
        ))}
      </Grid>

      <AccountEditModals
        editing={editing}
        finishEditing={finishEditing}
        onCancelConfirmCancelModal={onCancelConfirmCancelModal}
      />
      <Box
        mt={2.5}
        nonce={undefined}
        onResize={undefined}
        onResizeCapture={undefined}
      >
        <AccountInformation
          editOptions={AccountInformationOptions}
          editOptionClick={(value: string | null) => {
            editOptionClicked(value, setOpenPaymentsSelectionModal, setEditing);
          }}
        />
      </Box>
      <ViewOrderHistory />
      <PaymentsSelectionModal
        open={openPaymentsSelectionModal}
        onClose={togglePaymentsSelectionModal}
        onAddPaymentMethod={() => {
          setOpenPaymentsSelectionModal(false);
          setOpenPaymentsUpdateModal(true);
        }}
      />
      <RadioPaymentsModal
        isOpen={openPaymentsUpdateModal}
        onCloseHandle={togglePaymentsUpdateModal}
      />
      <PayPalRedirectionModal
        paypalState={paypalState}
        isOpen={openPaypalRedirection}
        onCloseHandle={togglePaypalRedirectionModal}
      />
    </>
  );
  // END RENDER
}
